/**
 * See exactly what the customer sees, without leaving the app.
 *
 * The owner, Sep 21 2026: "allow me to preview bids, quote, customers screen
 * from app and x out of them." Checking a bid meant opening the share link in
 * another tab and finding your way back.
 *
 * It is the real page in a frame, not a mock-up, so what he checks is what they
 * get. Read-only is not enforceable inside a frame, so it says plainly that it
 * is live: accepting here would really accept.
 */
import React, { useEffect, useState } from 'react';
import { X, ExternalLink, Smartphone, Monitor } from 'lucide-react';
import { officeLink } from '../utils/bidOpened';

// Both the frame and the open-in-a-tab carry ?from=office (utils/bidOpened): him looking is never "the customer opened it".
function frameUrl(url: string) {
  // The link we hand a customer can be the public site. The preview has to
  // open on the site you're already using — the API host refuses to be framed.
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin && /^\/(q|my|invoice|r)\//.test(u.pathname)) {
      return window.location.origin + u.pathname + u.search + u.hash;
    }
  } catch { /* keep the url as given */ }
  return url;
}

export default function CustomerView({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const [phone, setPhone] = useState(true);

  // Escape closes it, the way every other overlay in the app does.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
            <p className="text-[11px] text-gray-500 truncate">This is their real page — anything you tap here really happens.</p>
          </div>
          <button onClick={() => setPhone(p => !p)} title={phone ? 'See it full width' : 'See it on a phone'}
            className="press p-2 rounded-xl border-2 border-gray-200 text-gray-600 flex-shrink-0">
            {phone ? <Monitor className="h-4 w-4" /> : <Smartphone className="h-4 w-4" />}
          </button>
          <a href={officeLink(url)} target="_blank" rel="noreferrer" title="Open it in a tab"
            className="press p-2 rounded-xl border-2 border-gray-200 text-gray-600 flex-shrink-0"><ExternalLink className="h-4 w-4" /></a>
          <button onClick={onClose} aria-label="Close" title="Close"
            className="press p-2 rounded-xl bg-gray-900 text-white flex-shrink-0"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 bg-gray-100 overflow-auto flex justify-center">
          <iframe
            src={officeLink(frameUrl(url))}
            title={title}
            className={`h-full bg-white ${phone ? 'w-[390px] border-x border-gray-300' : 'w-full'}`}
          />
        </div>
      </div>
    </div>
  );
}
